import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import SmsFactorCard from "../components/SmsFactorCard";
import WaCloudCard, { WhatsAppProviderSwitch } from "../components/WaCloudCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import {
  MessageCircle, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, RefreshCw,
  Send, Plus, Trash2, Save, Eye, EyeOff, AlertTriangle, Clock
} from "lucide-react";

// Etichette user-facing per gli eventi supportati.
const EVENT_LABELS = {
  gift_card_received: {
    label: "Gift card ricevuta (destinatario)",
    hint: "Inviato al destinatario di una gift card acquistata online. Variabili consigliate: {{sender_name}} ti ha regalato una gift card da € {{amount}}: codice {{code}}, saldo e dettagli su {{link}}. Messaggio: {{message}}.",
  },
  gift_card_expiring: {
    label: "Gift card in scadenza (promemoria 30 giorni)",
    hint: "Inviato una sola volta al destinatario 30 giorni prima della scadenza se c'è saldo residuo. Es.: «Ciao {{recipient_name}}, sulla gift card {{code}} hai ancora € {{balance}}: scade il {{expires_at}} (tra {{days}} giorni). Saldo: {{link}}».",
  },
  gift_group_thanks: {
    label: "Regalo di gruppo: grazie al partecipante",
    hint: "Inviato a ogni amico che ha lasciato il numero WhatsApp quando la gift card di gruppo viene consegnata. Es.: «Grazie {{name}}! Con {{friends}} amici avete raccolto € {{total}} per {{recipient_name}}: la gift card è stata consegnata. Il tuo contributo: € {{contribution}}».",
  },
  gift_group_reminder: {
    label: "Regalo di gruppo: mancano 2 giorni (partecipanti)",
    hint: "Inviato a chi ha già contribuito quando mancano 2 giorni alla scadenza e l'obiettivo non è raggiunto. Es.: «Ciao {{name}}, per il regalo a {{recipient_name}} siamo a € {{collected}} su € {{target}}: mancano € {{missing}} entro il {{deadline}}. Inoltra il link: {{link}}».",
  },
  gift_group_contribution: {
    label: "Regalo di gruppo: nuovo contributo (organizzatore)",
    hint: "Inviato all'organizzatore (se ha lasciato il numero WhatsApp) ogni volta che un amico contribuisce. Es.: «Ciao {{organizer_name}}, {{contributor_name}} ha contribuito con € {{amount}} al regalo per {{recipient_name}}. Raccolti € {{collected}} su € {{target}} ({{percent}}%). Link: {{link}}».",
  },
  password_reset_link: {
    label: "Link reimposta password (WhatsApp)",
    hint: "Inviato al numero registrato quando il cliente chiede il reset password via WhatsApp. Template consigliato: «{{app_name}}: crea la nuova password entro {{minutes}} minuti → {{reset_url}}. Se non l'hai richiesto tu, ignora.» Senza automazione collegata il reset via WhatsApp viene rifiutato (resta l'email).",
    priority: "P0",
  },
  landing_promo: {
    label: "Invito landing · clienti con pochi punti",
    hint: "Inviato da Landing circuito → «Invita i clienti con pochi punti» (manuale o ogni lunedì). Es.: «Ciao {{first_name}}, hai {{points}} punti: questa settimana {{offers}}. Scopri tutto e ordina online → {{link}}». Max 1 invio ogni 30 giorni per cliente.",
  },
  catalog_published: {
    label: "Nuovo catalogo premi pubblicato",
    hint: "Inviato ai clienti quando pubblichi una versione del catalogo premi con il canale WhatsApp attivo (Raccolte punti → Catalogo grafico). Es.: «Ciao {{first_name}}, è arrivato il nuovo catalogo premi «{{label}}»! Sfoglialo qui: {{link}}».",
  },
  card_activation_welcome: {
    label: "Benvenuto attivazione carta",
    hint: "Inviato quando un nuovo cliente riceve la carta fedeltà. Consigliato nel template: «Salva la card nel Wallet del telefono: {{wallet_url}}» (il link apre Apple Wallet su iPhone e Google Wallet su Android).",
    priority: "P0",
  },
  reward_reserved: {
    label: "Conferma prenotazione premio",
    hint: "Inviato quando un cliente prenota un premio dal portale.",
    priority: "P1",
  },
  reward_ready: {
    label: "Premio pronto per il ritiro",
    hint: "Inviato quando lo staff segna il premio come pronto.",
    priority: "P1",
  },
  reward_picked_up: {
    label: "Conferma ritiro premio",
    hint: "Inviato al completamento della consegna del premio.",
    priority: "P1",
  },
  reward_pickup_reminder: {
    label: "Promemoria premio non ritirato",
    hint: "Inviato dal cron mattutino ai clienti con un premio «pronto» da almeno N giorni (Notifiche admin → Preferenze). Es.: «Ciao {{first_name}}, il premio «{{reward_name}}» ti aspetta: mostra in cassa il codice {{pickup_code}}».",
  },
  promo_campaign: {
    label: "Campagna promozionale",
    hint: "Richiede consenso marketing e template Marketing approvato. Se configurata, il lancio campagne (canale WhatsApp) invia automaticamente da qui.",
    priority: "P2",
  },
  issue_card_otp: {
    label: "OTP rilascio card (WhatsApp)",
    hint: "Codice di verifica a 6 cifre inviato al cliente durante Rilascio Card / Registrazione. Usa lo stesso template Authentication dell'OTP reset password (variabile otp_code).",
    priority: "P1",
  },
  password_reset_otp: {
    label: "OTP / Reset password",
    hint: "Richiede un template Authentication approvato da Meta.",
    priority: "P3 (verifica supporto Spoki)",
  },
  kpi_alert_admin: {
    label: "Alert calo KPI (solo admin)",
    hint: "Inviato al numero admin configurato in Statistiche → Alert calo KPI. Variabili: threshold, revenue_drop, tx_drop, summary.",
    priority: "Interno",
  },
  weekly_summary_admin: {
    label: "Riepilogo settimanale (solo admin)",
    hint: "Ogni lunedì alle 09:00 al numero admin (Statistiche → Alert KPI): fatturato, nuovi clienti, premi e classifica sedi della settimana. Aggiungi {{ai_insights}} al template per ricevere anche l'analisi AI con 3 consigli pratici.",
    priority: "Interno",
  },
  weekly_summary_branch: {
    label: "Report settimanale per sede (responsabili PV)",
    hint: "Ogni settimana (dal lunedì alle 09:00, appena il CRM ha gli scontrini di domenica) al numero WhatsApp del responsabile impostato in Punti vendita (solo circuiti con CRM Elastic): scontrini, incasso, scontrino medio e confronto con la settimana prima della propria sede. Aggiungi {{ai_insights}} al template per l'analisi AI con 3 consigli; {{rank}}, {{rank_delta}} e {{share_pct}} per la posizione in classifica catena e la quota sull'incasso.",
    priority: "Interno",
  },
  admin_reply: {
    label: "Risposta admin dal thread Messaggi",
    hint: "Usato quando rispondi a un cliente da Messaggi & News con 'Anche WhatsApp'. Il template deve contenere {{reply_text}}.",
    priority: "P1",
  },
  admin_alert: {
    label: "Avviso admin generico (ordini, prenotazioni, messaggi)",
    hint: "Al numero admin, per gli eventi con canale WhatsApp attivo in Notifiche admin → Preferenze. Template con {{title}} e {{body}}.",
    priority: "Interno",
  },
  order_in_prep: {
    label: "Ordine in preparazione con orario di ritiro",
    hint: "Inviato al cliente quando lo staff segna l'ordine 'In preparazione' indicando i minuti di attesa. Template con {{ready_time}} e {{items}}.",
    priority: "P1",
  },
  order_delayed: {
    label: "Scuse per il ritardo con nuovo orario",
    hint: "Inviato al cliente quando lo staff aggiorna l'attesa di un ordine già in preparazione (es. dopo l'avviso di ritardo). Template con {{ready_time}}, {{prev_time}}, {{delay_minutes}}.",
    priority: "P1",
  },
  coupon_expiring: {
    label: "Promemoria coupon in scadenza (domani)",
    hint: "Inviato ogni mattina (08:00) al cliente per ogni coupon non ancora usato che scade il giorno dopo. Template con {{coupon_name}}, {{discount}}, {{coupon_code}}, {{valid_until}}.",
    priority: "P2",
  },
  order_paid: {
    label: "Ricevuta ordine pagato (PayPal)",
    hint: "Inviato al cliente subito dopo il pagamento PayPal di un ordine dal portale. Template con {{amount}}, {{transaction_id}}, {{order_id}}, {{items}}.",
    priority: "P1",
  },
  wallet_reminder: {
    label: "Promemoria Wallet (card non salvata dopo 3 giorni)",
    hint: "Inviato ogni mattina (08:00), una sola volta per cliente, a chi ha la card da almeno 3 giorni e non l'ha ancora salvata in Apple/Google Wallet. Template con {{wallet_url}} (link 'Salva nel Wallet'), {{customer_name}}, {{card_number}}.",
    priority: "P2",
  },
  flash_offer: {
    label: "Offerta lampo (avvio)",
    hint: "Inviato ai clienti della sede (o di tutto il circuito) con consenso marketing WhatsApp quando parte un'offerta lampo da Sedi → Offerta lampo. Template con {{title}}, {{text}}, {{branch}}, {{until}} (ora di fine).",
    priority: "P2",
  },
};

// Variabili inviate a Spoki come `custom_fields` del contatto (usabili nel template WhatsApp).
// Standard per tutti gli eventi: first_name, last_name, email, phone.
const EVENT_VARIABLES = {
  gift_card_received: [
    ["recipient_name", "Nome destinatario"], ["sender_name", "Nome di chi regala"], ["amount", "Importo €"], ["code", "Codice gift card"],
    ["message", "Messaggio personale"], ["link", "Link pagina saldo"], ["app_name", "Nome circuito"],
  ],
  gift_card_expiring: [
    ["recipient_name", "Nome destinatario"], ["sender_name", "Nome di chi ha regalato"], ["balance", "Saldo residuo €"], ["code", "Codice gift card"],
    ["expires_at", "Data di scadenza (AAAA-MM-GG)"], ["days", "Giorni alla scadenza"], ["link", "Link pagina saldo"], ["app_name", "Nome circuito"],
  ],
  gift_group_thanks: [
    ["name", "Nome partecipante"], ["recipient_name", "Nome festeggiato"], ["total", "Totale raccolto €"], ["contribution", "Contributo del partecipante €"], ["friends", "Numero amici"],
    ["code", "Codice gift card"], ["link", "Link pagina del regalo"], ["organizer_name", "Organizzatore"], ["app_name", "Nome circuito"],
  ],
  gift_group_reminder: [
    ["name", "Nome partecipante"], ["recipient_name", "Nome festeggiato"], ["collected", "Raccolto €"], ["target", "Obiettivo €"], ["missing", "Mancano €"],
    ["deadline", "Data limite (GG/MM/AAAA)"], ["link", "Link da inoltrare"], ["app_name", "Nome circuito"],
  ],
  gift_group_contribution: [
    ["organizer_name", "Nome organizzatore"], ["contributor_name", "Nome di chi ha contribuito"], ["amount", "Importo contributo €"], ["recipient_name", "Nome festeggiato"],
    ["collected", "Raccolto €"], ["target", "Obiettivo €"], ["missing", "Mancano €"], ["percent", "Percentuale raggiunta"], ["friends", "Numero partecipanti"], ["link", "Link pagina del regalo"], ["code", "Codice regalo di gruppo"], ["app_name", "Nome circuito"],
  ],
  password_reset_link: [
    ["reset_url", "Link per creare la nuova password (30 min)"], ["card_number", "Numero carta"], ["customer_name", "Nome cliente"], ["minutes", "Minuti di validità"], ["app_name", "Nome circuito"],
  ],
  landing_promo: [
    ["first_name", "Nome cliente"], ["points", "Punti attuali"], ["link", "Link landing circuito (?src=whatsapp)"], ["offers", "Offerte della settimana (titoli news Offerte)"], ["app_name", "Nome circuito"],
  ],
  catalog_published: [
    ["first_name", "Nome cliente"], ["label", "Nome versione del catalogo (es. Natale)"], ["catalog_title", "Titolo del catalogo"], ["link", "Link pagina catalogo (?src=whatsapp)"], ["app_name", "Nome circuito"],
  ],
  card_activation_welcome: [
    ["card_number", "Numero carta fedeltà"],
    ["customer_name", "Nome completo cliente"],
    ["wallet_url", "Link «Salva la card nel Wallet» (Apple/Google in automatico)"],
    ["portal_url", "Link al portale cliente"],
  ],
  reward_reserved: [
    ["reward_name", "Nome del premio prenotato"],
    ["points_cost", "Punti richiesti"],
    ["pickup_date", "Data ritiro scelta (se indicata)"],
    ["card_number", "Numero carta"],
  ],
  reward_ready: [
    ["reward_name", "Nome del premio pronto"],
    ["points_cost", "Punti richiesti"],
    ["pickup_date", "Data ritiro"],
    ["card_number", "Numero carta"],
  ],
  reward_picked_up: [
    ["reward_name", "Premio ritirato"],
    ["points_cost", "Punti scalati"],
    ["card_number", "Numero carta"],
  ],
  reward_pickup_reminder: [
    ["first_name", "Nome cliente"], ["reward_name", "Premio prenotato"], ["pickup_code", "Codice ritiro (RP-XXXX)"], ["days", "Giorni dalla disponibilità"], ["card_number", "Numero carta"], ["app_name", "Nome circuito"],
  ],
  promo_campaign: [
    ["campaign_name", "Nome campagna"],
    ["message", "Testo promozionale"],
    ["coupon_code", "Codice coupon (se presente)"],
  ],
  issue_card_otp: [
    ["otp_code", "Codice OTP a 6 cifre (valido 10 min)"],
    ["otp", "Alias di otp_code"],
    ["minuti", "Minuti di validità"],
    ["app_name", "Nome dell'app"],
  ],
  password_reset_otp: [
    ["otp_code", "Codice OTP a 6 cifre (valido 10 min)"],
    ["card_number", "Numero carta"],
    ["app_name", "Nome dell'app"],
  ],
  kpi_alert_admin: [
    ["direction", "calo | picco"],
    ["threshold", "Soglia % configurata"],
    ["window_days", "Giorni della finestra"],
    ["revenue_drop", "Calo fatturato %"],
    ["tx_drop", "Calo transazioni %"],
    ["revenue", "Fatturato periodo corrente €"],
    ["prev_revenue", "Fatturato periodo precedente €"],
    ["tx_count", "Transazioni correnti"],
    ["prev_tx_count", "Transazioni precedenti"],
    ["summary", "Riassunto testuale completo"],
  ],
  weekly_summary_admin: [
    ["ai_insights", "Analisi AI della settimana + 3 consigli pratici (vuoto se disattivata)"],
    ["week_from", "Inizio settimana (gg/mm)"],
    ["week_to", "Fine settimana (gg/mm)"],
    ["revenue", "Fatturato settimana €"],
    ["revenue_delta", "Variazione % vs settimana prima (es. +12.5)"],
    ["tx_count", "Numero transazioni"],
    ["new_customers", "Nuovi clienti"],
    ["rewards_redeemed", "Premi riscattati"],
    ["top_reward", "Premio più riscattato"],
    ["points_earned", "Punti emessi"],
    ["coupons_used", "Coupon usati"],
    ["branches_ranking", "Classifica sedi (1. Nome € .. · 2. ..)"],
    ["top_branch", "Sede con più fatturato"],
    ["summary", "Riassunto testuale completo"],
  ],
  weekly_summary_branch: [
    ["ai_insights", "Analisi AI della sede + 3 consigli pratici (vuoto se disattivata)"],
    ["branch_name", "Nome del punto vendita"],
    ["manager_name", "Nome del responsabile"],
    ["week_from", "Inizio settimana (gg/mm)"],
    ["week_to", "Fine settimana (gg/mm)"],
    ["receipts", "Scontrini della settimana"],
    ["revenue", "Incasso settimana €"],
    ["avg_ticket", "Scontrino medio €"],
    ["cards", "Carte fedeltà attive"],
    ["points", "Punti assegnati"],
    ["delta_receipts", "Variazione scontrini vs settimana prima (es. +4,2%)"],
    ["delta_revenue", "Variazione incasso vs settimana prima"],
    ["delta_avg_ticket", "Variazione scontrino medio"],
    ["best_day", "Giorno migliore (es. sabato 14/06 (€ 1.234,00))"],
    ["peak_hour", "Fascia oraria con più scontrini"],
    ["top_products", "Primi 3 prodotti per incasso"],
    ["rank", "Posizione in classifica catena per incasso (es. 3ª su 118)"],
    ["rank_total", "Numero sedi in classifica"],
    ["rank_delta", "Variazione posizione incasso vs settimana prima (▲ 2 posizioni / ▼ 1 posizione / = stabile)"],
    ["rank_receipts", "Posizione in classifica per scontrini"],
    ["rank_receipts_delta", "Variazione posizione scontrini vs settimana prima"],
    ["share_pct", "Quota % della sede sull'incasso totale catena"],
    ["summary", "Riassunto testuale completo"],
  ],
  admin_reply: [
    ["reply_text", "Testo della risposta scritta dall'admin"],
    ["customer_name", "Nome completo cliente"],
    ["card_number", "Numero carta"],
  ],
  admin_alert: [
    ["title", "Titolo notifica (es. 🛍️ Nuovo ordine da Mario)"],
    ["body", "Dettaglio notifica"],
    ["kind", "Tipo evento: new_order | new_reservation | customer_reply | campaign_auto_launch"],
    ["customer_name", "Nome cliente (se presente)"],
    ["message", "Testo messaggio cliente (solo customer_reply)"],
  ],
  order_in_prep: [
    ["ready_time", "Orario stimato di ritiro (es. 13:45)"],
    ["eta_minutes", "Minuti di attesa indicati dallo staff"],
    ["items", "Riepilogo articoli (max 3)"],
    ["card_number", "Numero carta"],
  ],
  order_delayed: [
    ["ready_time", "Nuovo orario di ritiro (es. 14:05)"],
    ["prev_time", "Orario promesso in precedenza (es. 13:45)"],
    ["delay_minutes", "Minuti di ritardo rispetto alla promessa"],
    ["eta_minutes", "Nuova attesa in minuti da adesso"],
    ["items", "Riepilogo articoli (max 3)"],
    ["card_number", "Numero carta"],
  ],
  coupon_expiring: [
    ["coupon_name", "Nome del coupon"],
    ["discount", "Sconto (es. −10% o −€ 5,00)"],
    ["coupon_code", "Codice coupon"],
    ["valid_until", "Data di scadenza (domani, AAAA-MM-GG)"],
    ["card_number", "Numero carta"],
    ["app_name", "Nome del club"],
  ],
  order_paid: [
    ["amount", "Importo pagato (es. € 12,50)"],
    ["transaction_id", "Codice transazione PayPal"],
    ["order_id", "Codice ordine (8 caratteri)"],
    ["items", "Riepilogo articoli (max 3)"],
    ["card_number", "Numero carta"],
    ["app_name", "Nome del club"],
  ],
  wallet_reminder: [
    ["wallet_url", "Link 'Salva nel Wallet' (iPhone → Apple, Android → Google)"],
    ["portal_url", "Link alla card nel portale"],
    ["customer_name", "Nome cliente"],
    ["card_number", "Numero carta"],
    ["days", "Giorni dalla carta (3)"],
    ["app_name", "Nome del club"],
  ],
  flash_offer: [
    ["first_name", "Nome cliente"],
    ["title", "Titolo offerta lampo"],
    ["text", "Testo offerta"],
    ["branch", "Sede (o nome del club se tutte)"],
    ["until", "Ora di fine (HH:MM)"],
    ["card_number", "Numero carta"],
    ["app_name", "Nome del club"],
  ],
};
const STANDARD_VARIABLES = [["first_name", "Nome"], ["last_name", "Cognome"], ["email", "Email"], ["phone", "Telefono E.164"]];

function EventVariables({ evt, compact = false }) {
  const [open, setOpen] = useState(!compact);
  const vars = EVENT_VARIABLES[evt] || [];
  const copy = (k) => {
    navigator.clipboard?.writeText(`{{${k}}}`).then(() => toast.success(`{{${k}}} copiato`)).catch(() => {});
  };
  return (
    <div className="mt-1.5" data-testid={`spoki-vars-${evt}`}>
      {compact && (
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`spoki-vars-toggle-${evt}`}
          className="text-[11px] text-indigo-300 hover:text-indigo-200">
          {open ? "▲ Nascondi variabili" : `▼ Variabili template (${vars.length + STANDARD_VARIABLES.length})`}
        </button>
      )}
      {open && (
        <div className="mt-1 flex flex-wrap gap-1">
          {[...vars, ...STANDARD_VARIABLES].map(([k, d], i) => (
            <button key={k} type="button" onClick={() => copy(k)} title={`${d} — clicca per copiare {{${k}}}`}
              data-testid={`spoki-var-${evt}-${k}`}
              className={`text-[10px] font-mono px-1.5 py-0.5 rounded border transition ${i < vars.length ? "border-indigo-800/60 text-indigo-200 bg-indigo-500/10 hover:bg-indigo-500/20" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
              {`{{${k}}}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const DELIVERY_META = {
  sent:        { label: "✓ Inviato",      cls: "pill-muted" },
  delivered:   { label: "✓✓ Consegnato",  cls: "pill-mint" },
  read:        { label: "👁 Letto",        cls: "pill-gold" },
  failed:      { label: "✗ Fallito",      cls: "pill-red" },
  undelivered: { label: "✗ Non recapitato", cls: "pill-red" },
  error:       { label: "✗ Errore",       cls: "pill-red" },
};

const STATUS_META = {
  connected:            { icon: CheckCircle2, tone: "emerald", label: "Connesso" },
  not_ready:            { icon: AlertTriangle, tone: "amber",  label: "Canale non pronto" },
  sender_mismatch:      { icon: ShieldAlert,  tone: "amber",  label: "Numero non trovato" },
  invalid_credentials:  { icon: XCircle,      tone: "red",    label: "API Key non valida" },
  error:                { icon: XCircle,      tone: "red",    label: "Errore" },
  unknown:              { icon: Clock,        tone: "zinc",   label: "Non verificato" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const Icon = meta.icon;
  const cls = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    amber:   "bg-amber-500/15 text-amber-300 border-amber-500/40",
    red:     "bg-red-500/15 text-red-300 border-red-500/40",
    zinc:    "bg-zinc-500/15 text-zinc-300 border-zinc-700",
  }[meta.tone];
  return (
    <span data-testid="spoki-status-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}>
      <Icon className="h-3.5 w-3.5" /> {meta.label}
    </span>
  );
}

export default function SpokiSettings() {
  const [provider, setProvider] = useState("spoki");
  useEffect(() => { api.get("/whatsapp/provider").then((r) => setProvider(r.data.provider || "spoki")).catch(() => {}); }, []);
  const [config, setConfig] = useState(null);
  const [automations, setAutomations] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({ event_type: "card_activation_welcome", phone: "", first_name: "Test" });
  const [autoDialog, setAutoDialog] = useState(null); // { event_type, form }
  const [whEvents, setWhEvents] = useState(null); // null = hidden
  const [whBusy, setWhBusy] = useState(false);
  const [whOpenId, setWhOpenId] = useState(null);

  const loadWebhookEvents = async () => {
    setWhBusy(true);
    try {
      const { data } = await api.get("/spoki/webhook-events", { params: { limit: 20 } });
      setWhEvents(data);
      if (!data.count) toast.info("Nessun evento ricevuto finora: verifica l'URL su Spoki");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setWhBusy(false); }
  };

  // ------------------------------- Loaders -------------------------------
  const loadAll = async () => {
    try {
      const [c, a] = await Promise.all([
        api.get("/spoki/config"),
        api.get("/spoki/automations"),
      ]);
      setConfig(c.data);
      setAutomations(a.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data } = await api.get("/spoki/logs", { params: { limit: 30 } });
      setLogs(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoadingLogs(false); }
  };
  useEffect(() => { loadAll(); loadLogs(); }, []);

  // ------------------------------- Actions -------------------------------
  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: config.enabled,
        company_name: config.company_name,
        sender_phone: config.sender_phone,
        test_mode: config.test_mode,
        test_recipients: config.test_recipients || [],
      };
      // Invia api_key solo se l'utente l'ha appena digitata (non vuota e non ancora salvata)
      if (config._new_api_key) payload.api_key = config._new_api_key;
      const { data } = await api.put("/spoki/config", payload);
      setConfig(data);
      toast.success("Impostazioni Spoki salvate");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const verifyConnection = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/spoki/verify");
      setConfig(data);
      toast.success("Connessione Spoki verificata");
    } catch (err) { toast.error(formatApiError(err)); await loadAll(); }
    finally { setVerifying(false); }
  };

  const addTestRecipient = (val) => {
    const p = (val || "").trim();
    if (!p) return;
    const next = Array.from(new Set([...(config.test_recipients || []), p]));
    setConfig({ ...config, test_recipients: next });
  };
  const removeTestRecipient = (idx) => {
    const next = [...(config.test_recipients || [])];
    next.splice(idx, 1);
    setConfig({ ...config, test_recipients: next });
  };

  const openAutomationEditor = (event_type) => {
    const current = automations[event_type] || {};
    setAutoDialog({
      event_type,
      form: {
        automation_uuid: current.automation_uuid || "",
        secret: "",
        template_name: current.template_name || "",
        enabled: current.enabled || false,
      },
    });
  };
  const saveAutomation = async () => {
    if (!autoDialog) return;
    if (!autoDialog.form.automation_uuid || !autoDialog.form.secret) {
      toast.error("UUID e Secret sono obbligatori");
      return;
    }
    try {
      await api.put(`/spoki/automations/${autoDialog.event_type}`, autoDialog.form);
      toast.success("Automazione salvata");
      setAutoDialog(null);
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteAutomation = async (event_type) => {
    if (!window.confirm("Rimuovere la configurazione per questo evento?")) return;
    try {
      await api.delete(`/spoki/automations/${event_type}`);
      toast.success("Rimossa");
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doTestSend = async () => {
    try {
      const { data } = await api.post("/spoki/test-send", testForm);
      if (data.state === "accepted") toast.success("Messaggio accettato da Spoki (attendi consegna)");
      else if (data.state === "duplicate") toast.info("Richiesta già inviata (idempotente)");
      else toast.error(`Stato: ${data.state} — ${data.reason || ""}`);
      setTestOpen(false);
      loadLogs();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!config) {
    return (
      <div className="p-8 text-zinc-500" data-testid="spoki-loading">
        <RefreshCw className="h-4 w-4 animate-spin inline mr-2" /> Carico configurazione…
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-up" data-testid="spoki-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red"><MessageCircle className="h-3 w-3" /> INTEGRAZIONE</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">WhatsApp · Spoki o Cloud API &amp; SMS Factor</h1>
          <p className="text-zinc-400 mt-1">
            Collega l'account Spoki di questa installazione. Le credenziali sono cifrate e usate solo lato server.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={config.connection_status} />
          {config.last_verified_at && (
            <span className="text-[11px] text-zinc-500">
              Verificato: {new Date(config.last_verified_at).toLocaleString("it-IT")}
            </span>
          )}
        </div>
      </div>

      {/* Provider WhatsApp: Spoki oppure Cloud API (Business Manager del cliente) */}
      <WhatsAppProviderSwitch provider={provider} onChange={setProvider} />
      <WaCloudCard eventLabels={EVENT_LABELS} eventVars={EVENT_VARIABLES} />

      {/* Config card */}
      <div className="card-elev p-5 space-y-4" data-testid="spoki-config-card">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Credenziali & sender</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">Attiva integrazione</span>
            <Switch
              data-testid="spoki-enabled-toggle"
              checked={!!config.enabled}
              onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-300 mb-1 inline-block">Nome azienda</Label>
            <Input
              data-testid="spoki-company-name"
              value={config.company_name || ""}
              onChange={(e) => setConfig({ ...config, company_name: e.target.value })}
              placeholder="Studio Nouvelle"
              className="bg-zinc-950 border-zinc-800"
            />
          </div>
          <div>
            <Label className="text-zinc-300 mb-1 inline-block">Numero WhatsApp mittente (E.164)</Label>
            <Input
              data-testid="spoki-sender-phone"
              value={config.sender_phone || ""}
              onChange={(e) => setConfig({ ...config, sender_phone: e.target.value })}
              placeholder="+390612345678"
              className="bg-zinc-950 border-zinc-800 font-mono"
            />
          </div>
        </div>

        <div>
          <Label className="text-zinc-300 mb-1 inline-block">Spoki API Key</Label>
          <div className="relative">
            <Input
              data-testid="spoki-api-key"
              type={showApiKey ? "text" : "password"}
              value={config._new_api_key ?? (config.has_api_key ? config.api_key_masked : "")}
              onChange={(e) => setConfig({ ...config, _new_api_key: e.target.value })}
              placeholder={config.has_api_key ? "Sostituisci la chiave attuale…" : "Incolla qui la tua Spoki API Key"}
              className="bg-zinc-950 border-zinc-800 font-mono pr-10"
            />
            <button
              type="button"
              onClick={() => setShowApiKey((s) => !s)}
              data-testid="spoki-api-key-toggle"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
              title={showApiKey ? "Nascondi" : "Mostra"}
            >
              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">
            La chiave viene cifrata prima del salvataggio. La riverifica è richiesta ad ogni modifica.
          </p>
        </div>

        {config.last_verify_error && config.connection_status !== "connected" && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-200"
               data-testid="spoki-verify-error">
            <AlertTriangle className="h-4 w-4 inline mr-1" /> {config.last_verify_error}
          </div>
        )}

        {config.channel_snapshot && (
          <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3 text-xs" data-testid="spoki-channel-snapshot">
            <div className="text-zinc-400 mb-1 font-semibold">Canale rilevato su Spoki</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-zinc-300">
              <div><span className="text-zinc-500">ID:</span> {config.channel_snapshot.id ?? "—"}</div>
              <div><span className="text-zinc-500">Phone:</span> <span className="font-mono">{config.channel_snapshot.phone || "—"}</span></div>
              <div><span className="text-zinc-500">Piattaforma:</span> {config.channel_snapshot.platform || "—"}</div>
              <div><span className="text-zinc-500">Attivo:</span> {String(config.channel_snapshot.is_active ?? "—")}</div>
              <div><span className="text-zinc-500">Stato:</span> {config.channel_snapshot.phone_status || "—"}</div>
              {config.channel_snapshot.quality_score != null && <div><span className="text-zinc-500">Qualità:</span> {config.channel_snapshot.quality_score}</div>}
              {config.channel_snapshot.daily_limit != null && <div><span className="text-zinc-500">Limite gg:</span> {config.channel_snapshot.daily_limit}</div>}
              <div><span className="text-zinc-500">Verificato Meta:</span> {String(config.channel_snapshot.has_official_verification ?? "—")}</div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button data-testid="spoki-save" onClick={saveConfig} disabled={saving}
            className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Salvo…" : "Salva impostazioni"}
          </Button>
          <Button data-testid="spoki-verify" variant="outline" onClick={verifyConnection}
            disabled={verifying || !config.has_api_key}
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
            <ShieldCheck className="h-4 w-4 mr-2" /> {verifying ? "Verifico…" : "Verifica connessione"}
          </Button>
        </div>
        {config.webhook_token && (
          <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-1" data-testid="spoki-webhook-card">
            <div className="text-xs font-semibold text-zinc-300">Webhook stato consegna (sent / delivered / read)</div>
            <p className="text-[11px] text-zinc-500">
              Su Spoki → Impostazioni → Webhook incolla questo URL per ricevere gli stati di consegna e lettura nei log qui sotto.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] font-mono bg-zinc-900 border border-zinc-800 rounded px-2 py-1 truncate" data-testid="spoki-webhook-url">
                {`${process.env.REACT_APP_BACKEND_URL}/api/spoki/webhook?token=${config.webhook_token}`}
              </code>
              <Button size="sm" variant="outline" className="border-zinc-800 h-8" data-testid="spoki-webhook-copy"
                onClick={() => navigator.clipboard?.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/spoki/webhook?token=${config.webhook_token}`).then(() => toast.success("URL webhook copiato"))}>
                Copia
              </Button>
              <Button size="sm" variant="outline" className="border-indigo-800/60 text-indigo-300 hover:bg-indigo-500/10 h-8" data-testid="spoki-webhook-events-btn"
                onClick={() => (whEvents ? setWhEvents(null) : loadWebhookEvents())} disabled={whBusy}>
                {whBusy ? "Carico…" : whEvents ? "Nascondi eventi" : "Ultimi eventi ricevuti"}
              </Button>
            </div>
            {whEvents && (
              <div className="mt-2 space-y-1" data-testid="spoki-webhook-events">
                <div className="flex items-center justify-between text-[11px] text-zinc-500">
                  <span>{whEvents.count} eventi totali · ultimi {whEvents.events.length}</span>
                  <button type="button" onClick={loadWebhookEvents} className="text-indigo-300 hover:text-indigo-200" data-testid="spoki-webhook-events-refresh">↻ Aggiorna</button>
                </div>
                {whEvents.events.length === 0 && (
                  <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2" data-testid="spoki-webhook-empty">
                    Nessun evento ricevuto. Incolla l'URL qui sopra nelle impostazioni webhook di Spoki, invia un messaggio di test e premi "Aggiorna".
                  </div>
                )}
                {whEvents.events.map((e) => (
                  <div key={e.id} className="text-[11px] bg-zinc-900/70 border border-zinc-800 rounded p-2" data-testid={`spoki-webhook-event-${e.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-zinc-400">{new Date(e.received_at).toLocaleString("it-IT")}</span>
                      <button type="button" className="text-indigo-300 hover:text-indigo-200" onClick={() => setWhOpenId(whOpenId === e.id ? null : e.id)}>
                        {whOpenId === e.id ? "nascondi JSON" : "JSON"}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {e.statuses.map((s, i) => <span key={i} className="pill pill-muted">{s.status} → {s.phone}</span>)}
                      {e.inbound.map((m, i) => <span key={`i${i}`} className="pill pill-mint">💬 {m.phone}: {m.text}</span>)}
                      {!e.statuses.length && !e.inbound.length && <span className="pill pill-muted">evento non riconosciuto (vedi JSON)</span>}
                    </div>
                    {whOpenId === e.id && (
                      <pre className="mt-1 text-[10px] font-mono bg-zinc-950 border border-zinc-800 rounded p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(e.payload, null, 2)}</pre>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Test recipients */}
      <div className="card-elev p-5 space-y-3" data-testid="spoki-test-recipients-card">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Modalità test</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">Solo whitelist</span>
            <Switch
              data-testid="spoki-testmode-toggle"
              checked={!!config.test_mode}
              onCheckedChange={(v) => setConfig({ ...config, test_mode: v })}
            />
          </div>
        </div>
        <p className="text-xs text-zinc-500">
          Con la modalità test attiva, gli invii automatici raggiungono solo i numeri elencati qui sotto.
          Aggiungi il tuo numero e altri consensualmente autorizzati per la beta.
        </p>
        <div className="flex flex-wrap gap-2" data-testid="spoki-whitelist">
          {(config.test_recipients || []).length === 0 && (
            <div className="text-xs text-zinc-500 italic">Nessun destinatario di test.</div>
          )}
          {(config.test_recipients || []).map((r, i) => (
            <span key={r}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-950 border border-zinc-800 text-sm font-mono"
              data-testid={`spoki-whitelist-${i}`}>
              {r}
              <button onClick={() => removeTestRecipient(i)}
                data-testid={`spoki-whitelist-remove-${i}`}
                className="ml-1 text-zinc-500 hover:text-red-300">
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 max-w-md">
          <Input placeholder="+393331234567" data-testid="spoki-whitelist-input"
            className="bg-zinc-950 border-zinc-800 font-mono"
            onKeyDown={(e) => {
              if (e.key === "Enter") { addTestRecipient(e.target.value); e.target.value = ""; }
            }} />
          <Button variant="outline" data-testid="spoki-whitelist-add"
            onClick={(e) => {
              const inp = e.currentTarget.parentElement.querySelector("input");
              addTestRecipient(inp.value); inp.value = "";
            }}
            className="border-zinc-800">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi
          </Button>
        </div>
        <Button data-testid="spoki-open-testsend" variant="outline"
          disabled={config.connection_status !== "connected"}
          onClick={() => setTestOpen(true)}
          className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10">
          <Send className="h-4 w-4 mr-2" /> Invia messaggio di test
        </Button>
      </div>

      {/* Automations */}
      <div className="card-elev p-5 space-y-3" data-testid="spoki-automations-card">
        <h2 className="font-heading text-lg font-bold">Automazioni per evento</h2>
        <p className="text-xs text-zinc-500">
          Su Spoki crea una <b>Automazione</b> per ciascun evento (con lo step "API startup" e un template WhatsApp approvato).
          Copia qui il suo <b>UUID</b> e <b>Secret</b>.
        </p>
        <div className="space-y-2">
          {Object.entries(EVENT_LABELS).map(([evt, meta]) => {
            const cfg = automations[evt] || {};
            const configured = cfg.has_secret;
            return (
              <div key={evt}
                className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-between gap-3"
                data-testid={`spoki-auto-row-${evt}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <span>{meta.label}</span>
                    <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-zinc-700 text-zinc-400">
                      {meta.priority}
                    </Badge>
                    {cfg.enabled && configured && <Badge className="bg-emerald-500/20 text-emerald-300 text-[10px] py-0 px-1.5">ATTIVO</Badge>}
                  </div>
                  <div className="text-[11px] text-zinc-500">{meta.hint}</div>
                  <EventVariables evt={evt} compact />
                  {configured ? (
                    <div className="text-[11px] text-zinc-400 mt-1 font-mono truncate">
                      UUID: {cfg.automation_uuid} · Secret: {cfg.secret_masked}
                      {cfg.template_name ? ` · Template: ${cfg.template_name}` : ""}
                    </div>
                  ) : (
                    <div className="text-[11px] text-amber-400/80 mt-1">Non configurata</div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => openAutomationEditor(evt)}
                    data-testid={`spoki-auto-edit-${evt}`} className="border-zinc-800">
                    {configured ? "Modifica" : "Configura"}
                  </Button>
                  {configured && (
                    <Button size="sm" variant="outline" onClick={() => deleteAutomation(evt)}
                      data-testid={`spoki-auto-delete-${evt}`}
                      className="border-zinc-800 hover:border-red-500 hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Logs */}
      <div className="card-elev p-5 space-y-3" data-testid="spoki-logs-card">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Ultimi invii</h2>
          <Button size="sm" variant="outline" onClick={loadLogs} disabled={loadingLogs}
            data-testid="spoki-logs-refresh" className="border-zinc-800">
            <RefreshCw className={`h-4 w-4 mr-1 ${loadingLogs ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
        </div>
        {logs.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4 text-center">Nessun invio registrato.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                <tr className="text-left">
                  <th className="p-2">Quando</th>
                  <th className="p-2">Evento</th>
                  <th className="p-2">Telefono</th>
                  <th className="p-2">Stato</th>
                  <th className="p-2">Note</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-zinc-900" data-testid={`spoki-log-${l.id}`}>
                    <td className="p-2 text-zinc-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString("it-IT")}</td>
                    <td className="p-2">{EVENT_LABELS[l.event_type]?.label || l.event_type}</td>
                    <td className="p-2 font-mono">{l.phone_e164}</td>
                    <td className="p-2">
                      <span className={`pill ${l.state === "accepted" ? "pill-mint" : l.state === "failed" ? "pill-red" : "pill-muted"}`}>
                        {l.state}
                      </span>
                      {l.delivery_status && (
                        <span className={`pill ml-1 ${(DELIVERY_META[l.delivery_status] || {}).cls || "pill-muted"}`}
                          title={l.delivery_updated_at ? `Aggiornato ${new Date(l.delivery_updated_at).toLocaleString("it-IT")}` : ""}
                          data-testid={`spoki-log-delivery-${l.id}`}>
                          {(DELIVERY_META[l.delivery_status] || {}).label || l.delivery_status}
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-zinc-500 truncate max-w-[280px]">{l.reason || (l.provider_response ? "OK" : "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Automation editor dialog */}
      <Dialog open={!!autoDialog} onOpenChange={(o) => !o && setAutoDialog(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-lg" data-testid="spoki-auto-dialog">
          <DialogHeader><DialogTitle>Configura automazione: {autoDialog && EVENT_LABELS[autoDialog.event_type]?.label}</DialogTitle></DialogHeader>
          {autoDialog && (
            <div className="space-y-3">
              <div>
                <Label className="text-zinc-300 mb-1 inline-block">Automation UUID</Label>
                <Input data-testid="spoki-auto-uuid" value={autoDialog.form.automation_uuid}
                  onChange={(e) => setAutoDialog({ ...autoDialog, form: { ...autoDialog.form, automation_uuid: e.target.value } })}
                  className="bg-zinc-950 border-zinc-800 font-mono" placeholder="9f2c…"/>
              </div>
              <div>
                <Label className="text-zinc-300 mb-1 inline-block">Secret</Label>
                <Input data-testid="spoki-auto-secret" type="password" value={autoDialog.form.secret}
                  onChange={(e) => setAutoDialog({ ...autoDialog, form: { ...autoDialog.form, secret: e.target.value } })}
                  className="bg-zinc-950 border-zinc-800 font-mono" placeholder="incolla il secret Spoki"/>
              </div>
              <div>
                <Label className="text-zinc-300 mb-1 inline-block">Nome template (informativo)</Label>
                <Input data-testid="spoki-auto-template" value={autoDialog.form.template_name}
                  onChange={(e) => setAutoDialog({ ...autoDialog, form: { ...autoDialog.form, template_name: e.target.value } })}
                  className="bg-zinc-950 border-zinc-800" placeholder="es. welcome_it"/>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                <span className="text-sm">Attiva questa automazione</span>
                <Switch data-testid="spoki-auto-enabled" checked={autoDialog.form.enabled}
                  onCheckedChange={(v) => setAutoDialog({ ...autoDialog, form: { ...autoDialog.form, enabled: v } })}/>
              </div>
              <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                <div className="text-xs text-zinc-300 font-semibold">Variabili disponibili nel template</div>
                <p className="text-[11px] text-zinc-500">Arrivano a Spoki come campi personalizzati del contatto. Clicca per copiare.</p>
                <EventVariables evt={autoDialog.event_type} />
              </div>
              <Button data-testid="spoki-auto-save" onClick={saveAutomation}
                className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
                <Save className="h-4 w-4 mr-2" /> Salva
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test send dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md" data-testid="spoki-testsend-dialog">
          <DialogHeader><DialogTitle>Invia messaggio di test</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Evento</Label>
              <select data-testid="spoki-testsend-event"
                value={testForm.event_type}
                onChange={(e) => setTestForm({ ...testForm, event_type: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-md h-10 px-3 text-sm">
                {Object.entries(EVENT_LABELS).map(([k, v]) => (
                  <option key={k} value={k} disabled={!automations[k]?.has_secret || !automations[k]?.enabled}>
                    {v.label} {automations[k]?.has_secret ? "" : "(non configurata)"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Destinatario (da whitelist)</Label>
              <select data-testid="spoki-testsend-phone"
                value={testForm.phone}
                onChange={(e) => setTestForm({ ...testForm, phone: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-md h-10 px-3 text-sm font-mono">
                <option value="">— Seleziona —</option>
                {(config.test_recipients || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Nome</Label>
              <Input data-testid="spoki-testsend-name" value={testForm.first_name}
                onChange={(e) => setTestForm({ ...testForm, first_name: e.target.value })}
                className="bg-zinc-950 border-zinc-800"/>
            </div>
            <Button data-testid="spoki-testsend-submit" onClick={doTestSend} disabled={!testForm.phone}
              className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              <Send className="h-4 w-4 mr-2" /> Invia
            </Button>
            <p className="text-[11px] text-zinc-500">
              Un "accepted" indica solo che Spoki ha preso in carico l'invio: la consegna definitiva su WhatsApp
              verrà tracciata via webhook (P2).
            </p>
          </div>
        </DialogContent>
      </Dialog>
      <SmsFactorCard />
    </div>
  );
}
